import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, MapPin, Phone, Star, Clock, Award, Zap, Camera, Bell, Home, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import DOMPurify from 'dompurify';

const AlarmeVarPage = () => {
  const cities = [
    'Toulon', 'Fréjus', 'Hyères', 'Draguignan', 'Saint-Raphaël', 'Six-Fours-les-Plages',
    'La Seyne-sur-Mer', 'Sanary-sur-Mer', 'Bandol', 'La Garde', 'La Valette-du-Var',
    'Ollioules', 'Le Pradet', 'Carqueiranne', 'La Crau', 'Sainte-Maxime',
    'Saint-Tropez', 'Cogolin', 'Grimaud', 'Le Lavandou', 'Bormes-les-Mimosas',
    'Brignoles', 'Saint-Maximin-la-Sainte-Baume', 'Le Luc', 'Vidauban'
  ];

  const advantages = [
    { icon: Shield, title: "Expert Ajax Systems", desc: "Installateur certifié Ajax Systems dans le Var" },
    { icon: Clock, title: "Intervention rapide", desc: "Installation sous 72h dans tout le département 83" },
    { icon: Award, title: "Garantie 4 ans", desc: "Tous nos produits Ajax sont garantis 4 ans" },
    { icon: Zap, title: "Autonomie exceptionnelle", desc: "Jusqu'à 7 ans d'autonomie sur batterie" },
  ];

  const services = [
    { icon: Bell, title: "Alarme sans fil Ajax", desc: "Systèmes d'alarme Grade 2 et Grade 3 pour villas" },
    { icon: Camera, title: "Vidéosurveillance IA", desc: "Caméras avec détection intelligente pour résidences secondaires" },
    { icon: Home, title: "Domotique connectée", desc: "Automatisation complète de votre villa provençale" },
    { icon: Building, title: "Sécurité entreprise", desc: "Solutions pour hôtels, restaurants, commerces" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "FGS Alarme Montpellier - Alarme Ajax Systems Var",
    "description": "Installation alarme et vidéosurveillance Ajax Systems dans le Var (83). Toulon, Fréjus, Saint-Tropez.",
    "url": "https://maprotection-ia.fr/alarme-ajax-var",
    "telephone": "+33745040929",
    "areaServed": { "@type": "State", "name": "Var" },
    "priceRange": "€€"
  };

  return (
    <>
      <Helmet>
        <title>Alarme Var : Installation Ajax Systems Toulon Fréjus (83) | Devis Gratuit | FGS Alarme</title>
        <meta name="description" content="★ Installateur alarme Var certifié Ajax Systems. Alarme maison sans fil, vidéosurveillance IA, télésurveillance 24/7. Intervention Toulon, Fréjus, Hyères sous 48h. Devis gratuit. ☎ 07 45 04 09 29" />
        <meta name="keywords" content="alarme ajax systems var, installation alarme toulon, vidéosurveillance fréjus, caméra saint-tropez, alarme villa var, alarme hyères, prix alarme var" />
        <link rel="canonical" href="https://maprotection-ia.fr/alarme-ajax-var" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="FR-83" />
        <meta name="geo.placename" content="Toulon" />
        <meta name="geo.position" content="43.1242;5.9280" />
        <meta name="ICBM" content="43.1242, 5.9280" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Alarme Var : Installation Ajax Systems (83) | Devis Gratuit" />
        <meta property="og:description" content="Installateur certifié Ajax Systems dans le Var. Alarme sans fil, vidéosurveillance IA, garantie 4 ans. Devis gratuit sous 24h." />
        <meta property="og:url" content="https://maprotection-ia.fr/alarme-ajax-var" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:image" content="https://maprotection-ia.fr/og-image-fb.png" />
        
        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(JSON.stringify(structuredData)) }} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maprotection-ia.fr" },
              { "@type": "ListItem", "position": 2, "name": "Alarme Var", "item": "https://maprotection-ia.fr/alarme-ajax-var" }
            ]
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-black/40">
        <section className="relative py-20 bg-gradient-to-b from-fgs-surface to-fgs-bg">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 mb-6">
              <MapPin className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Département du Var (83)</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center text-white mb-6">
              Installation <span className="text-emerald-400">Alarme Ajax Systems</span><br />dans le Var
            </h1>
            
            <p className="text-xl text-white/50 text-center max-w-3xl mx-auto mb-8">
              FGS Alarme Montpellier installe vos systèmes d'alarme Ajax Systems à Toulon, Fréjus, Saint-Tropez et sur la Côte d'Azur. Garantie 4 ans.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold">
                <a href="tel:+33745040929"><Phone className="w-5 h-5 mr-2" />07 45 04 09 29</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-fgs-primary text-emerald-400 hover:bg-emerald-500/10">
                <Link to="/contact">Devis gratuit en 24h</Link>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-12">
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="text-white font-medium">4.9/5</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Certifié Ajax Systems</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.02] px-4 py-2 rounded-full">
                <Award className="w-5 h-5 text-emerald-400" />
                <span className="text-white">Garantie 4 ans</span>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-12">Pourquoi choisir FGS Alarme Montpellier dans le Var ?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((item, index) => (
                <div key={index} className="bg-white/[0.02] p-6 rounded-xl border border-white/5 hover:border-fgs-primary transition-colors">
                  <item.icon className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-white/50">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white/[0.02]">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-4">Nos services dans le Var</h2>
            <p className="text-white/50 text-center max-w-2xl mx-auto mb-12">Solutions Ajax Systems pour villas et résidences de la Côte d'Azur</p>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {services.map((service, index) => (
                <div key={index} className="flex gap-4 p-6 bg-black/40 rounded-xl border border-white/5">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-white/50">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-black/40">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-white mb-4">Zone d'intervention dans le Var</h2>
            <p className="text-white/50 text-center max-w-2xl mx-auto mb-12">De Toulon à Saint-Tropez</p>
            <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
              {cities.map((city, index) => (
                <span key={index} className="px-3 py-1 bg-white/[0.02] border border-white/5 rounded-full text-sm text-white/50 hover:text-emerald-400 hover:border-fgs-primary transition-colors">{city}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-r from-fgs-primary/20 to-fgs-surface">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Protégez votre villa ou commerce dans le Var</h2>
            <p className="text-white/50 max-w-2xl mx-auto mb-8">Devis gratuit sous 24h</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-500/90 text-black font-semibold">
                <a href="tel:+33745040929"><Phone className="w-5 h-5 mr-2" />Appeler maintenant</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Link to="/shop">Voir nos produits</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AlarmeVarPage;
